import Project from 'models/Project';
import { getErrorMessage } from './errorMessages';

/**
 * Validates multiple fields against the defined validation rules and returns a formatted error object.
 *
 * @param {Object} params - The parameters for the validation function.
 * @param {Array<string>} params.fields - An array of field names to be validated.
 * @param {Object} params.data - The data object containing field values.
 * @param {string} [params.language='nl'] - The language for error messages (default is 'nl').
 *
 * @returns {Object|null} - Returns an object with the field names as keys and their error messages as values,
 *                          or null if there are no errors.
 *
 * @example
 * const fields = ['firstName', 'lastName', 'email'];
 * const data = { firstName: 'John', lastName: '', email: 'invalid-email' };
 * const language = 'en';
 * const errors = validateMultipleFields({ fields, data, language });
 * console.log(errors); // { lastName: 'Last name is required.', email: 'Invalid email address.' }
 */
const validateMultipleFields = ({ fields, data, language = 'nl' }) => {
  const errors = {};

  for (const fieldName of fields) {
    const value = data[fieldName];
    const validationResponse = Project.validateField(fieldName, value);

    if (validationResponse) {
      errors[fieldName] = getErrorMessage(validationResponse, language);
    }
  }

  return Object.keys(errors).length === 0 ? null : errors;
};

/**
 * Validates a step in a form by validating multiple fields.
 * @function
 * @param {Object} params - The input parameters.
 * @param {Array<string>} params.fields - An array of field names to be validated.
 * @param {Object} params.data - An object containing field values.
 * @returns {Object|null} - An object containing field names as keys and validation error messages as values or null if there are no errors.
 */
const validateStep = ({ fields, data }) =>
  validateMultipleFields({ fields, data });

/**
 * Validates the entire form by validating all fields in the provided data object.
 * @function
 * @param {Object} params - The input parameters.
 * @param {Object} params.data - An object containing field values.
 * @returns {Object|null} - An object containing field names as keys and validation error messages as values or null if there are no errors.
 */
const validateForm = ({ data }) => {
  const allFormFields = Object.keys(data);
  return validateMultipleFields({ fields: allFormFields, data });
};

export { validateStep, validateForm, validateMultipleFields };
